import logger from "../utils/logger";
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Building2, Plus, GitBranch, Briefcase, Clock, DollarSign, Calendar, Trash2 } from 'lucide-react';

import API_URL from '../utils/api';

const TabButton = ({ active, onClick, icon: Icon, label, count }) => (
    <button
        onClick={onClick}
        className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
            active ? 'border-brand text-brand' : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
        }`}
        data-testid={`tab-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
        <Icon className="h-4 w-4" />
        {label}
        {count > 0 && <Badge className="rounded-none bg-slate-100 text-slate-700 text-xs ml-1">{count}</Badge>}
    </button>
);

const CompanySetup = () => {
    const [activeTab, setActiveTab] = useState('companies');
    const [companies, setCompanies] = useState([]);
    const [branches, setBranches] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [shifts, setShifts] = useState([]);
    const [salaryStructures, setSalaryStructures] = useState([]);
    const [leaveStructures, setLeaveStructures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);

    // Form states
    const [companyForm, setCompanyForm] = useState({ name: '', code: '', address: '', phone: '', email: '', website: '', tax_id: '' });
    const [branchForm, setBranchForm] = useState({ name: '', code: '', company_id: '', address: '', phone: '', is_head_office: false });
    const [deptForm, setDeptForm] = useState({ name: '', code: '', description: '' });
    const [desigForm, setDesigForm] = useState({ name: '', code: '', level: 1 });
    const [shiftForm, setShiftForm] = useState({ name: '', start_time: '09:00', end_time: '18:00', break_duration_minutes: 60, is_night_shift: false });
    const [salaryForm, setSalaryForm] = useState({ name: '', basic_percentage: 50, hra_percentage: 20, da_percentage: 10, pf_percentage: 12, esi_percentage: 0.75, professional_tax: 200 });
    const [leaveForm, setLeaveForm] = useState({ name: '', annual_leaves: 20, sick_leaves: 10, casual_leaves: 5, maternity_leaves: 90, paternity_leaves: 15 });

    useEffect(() => { fetchAll(); }, []);

    const fetchAll = async () => {
        try {
            const [comp, br, dept, desig, sh, sal, lv] = await Promise.all([
                axios.get(`${API_URL}/api/companies`, { withCredentials: true }),
                axios.get(`${API_URL}/api/branches`, { withCredentials: true }),
                axios.get(`${API_URL}/api/departments`, { withCredentials: true }),
                axios.get(`${API_URL}/api/designations`, { withCredentials: true }),
                axios.get(`${API_URL}/api/shifts`, { withCredentials: true }),
                axios.get(`${API_URL}/api/salary-structures`, { withCredentials: true }),
                axios.get(`${API_URL}/api/leave-structures`, { withCredentials: true }),
            ]);
            setCompanies(comp.data);
            setBranches(br.data);
            setDepartments(dept.data);
            setDesignations(desig.data);
            setShifts(sh.data);
            setSalaryStructures(sal.data);
            setLeaveStructures(lv.data);
        } catch (error) {
            logger.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async (endpoint, data) => {
        try {
            await axios.post(`${API_URL}/api/${endpoint}`, data, { withCredentials: true });
            setDialogOpen(false);
            fetchAll();
        } catch (error) {
            alert(error.response?.data?.detail || 'Error creating record');
        }
    };

    const tabs = [
        { id: 'companies', label: 'Companies', icon: Building2, count: companies.length },
        { id: 'branches', label: 'Branches', icon: GitBranch, count: branches.length },
        { id: 'departments', label: 'Departments', icon: Building2, count: departments.length },
        { id: 'designations', label: 'Designations', icon: Briefcase, count: designations.length },
        { id: 'shifts', label: 'Shifts', icon: Clock, count: shifts.length },
        { id: 'salary', label: 'Salary Structures', icon: DollarSign, count: salaryStructures.length },
        { id: 'leave', label: 'Leave Structures', icon: Calendar, count: leaveStructures.length },
    ];

    const renderForm = () => {
        switch (activeTab) {
            case 'companies':
                return (
                    <form onSubmit={(e) => { e.preventDefault(); handleCreate('companies', companyForm); }} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Company Name</Label>
                                <Input value={companyForm.name} onChange={(e) => setCompanyForm({...companyForm, name: e.target.value})} className="rounded-none mt-1" required data-testid="company-name-input" /></div>
                            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Code</Label>
                                <Input value={companyForm.code} onChange={(e) => setCompanyForm({...companyForm, code: e.target.value.toUpperCase()})} className="rounded-none mt-1" required data-testid="company-code-input" /></div>
                        </div>
                        <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Address</Label>
                            <Input value={companyForm.address} onChange={(e) => setCompanyForm({...companyForm, address: e.target.value})} className="rounded-none mt-1" /></div>
                        <div className="grid grid-cols-2 gap-4">
                            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Phone</Label>
                                <Input value={companyForm.phone} onChange={(e) => setCompanyForm({...companyForm, phone: e.target.value})} className="rounded-none mt-1" /></div>
                            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Email</Label>
                                <Input type="email" value={companyForm.email} onChange={(e) => setCompanyForm({...companyForm, email: e.target.value})} className="rounded-none mt-1" /></div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Website</Label>
                                <Input value={companyForm.website} onChange={(e) => setCompanyForm({...companyForm, website: e.target.value})} className="rounded-none mt-1" /></div>
                            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Tax ID</Label>
                                <Input value={companyForm.tax_id} onChange={(e) => setCompanyForm({...companyForm, tax_id: e.target.value})} className="rounded-none mt-1" /></div>
                        </div>
                        <div className="flex justify-end gap-3 pt-4">
                            <Button type="button" variant="outline" className="rounded-none" onClick={() => setDialogOpen(false)}>Cancel</Button>
                            <Button type="submit" className="bg-brand hover:bg-brand rounded-none" data-testid="save-company-btn">Create Company</Button>
                        </div>
                    </form>
                );
            case 'branches':
                return (
                    <form onSubmit={(e) => { e.preventDefault(); handleCreate('branches', branchForm); }} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Branch Name</Label>
                                <Input value={branchForm.name} onChange={(e) => setBranchForm({...branchForm, name: e.target.value})} className="rounded-none mt-1" required data-testid="branch-name-input" /></div>
                            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Code</Label>
                                <Input value={branchForm.code} onChange={(e) => setBranchForm({...branchForm, code: e.target.value.toUpperCase()})} className="rounded-none mt-1" required /></div>
                        </div>
                        <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Company</Label>
                            <select value={branchForm.company_id} onChange={(e) => setBranchForm({...branchForm, company_id: e.target.value})} className="w-full rounded-none border border-slate-200 px-3 py-2 text-sm mt-1" required>
                                <option value="">Select Company</option>
                                {companies.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                            </select></div>
                        <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Address</Label>
                            <Input value={branchForm.address} onChange={(e) => setBranchForm({...branchForm, address: e.target.value})} className="rounded-none mt-1" /></div>
                        <div className="flex items-center gap-2 mt-2">
                            <input type="checkbox" checked={branchForm.is_head_office} onChange={(e) => setBranchForm({...branchForm, is_head_office: e.target.checked})} id="head-office" />
                            <Label htmlFor="head-office" className="text-sm">Head Office</Label>
                        </div>
                        <div className="flex justify-end gap-3 pt-4">
                            <Button type="button" variant="outline" className="rounded-none" onClick={() => setDialogOpen(false)}>Cancel</Button>
                            <Button type="submit" className="bg-brand hover:bg-brand rounded-none" data-testid="save-branch-btn">Create Branch</Button>
                        </div>
                    </form>
                );
            case 'departments':
                return (
                    <form onSubmit={(e) => { e.preventDefault(); handleCreate('departments', deptForm); }} className="space-y-4">
                        <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Department Name</Label>
                            <Input value={deptForm.name} onChange={(e) => setDeptForm({...deptForm, name: e.target.value})} className="rounded-none mt-1" required data-testid="dept-name-input" /></div>
                        <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Code</Label>
                            <Input value={deptForm.code} onChange={(e) => setDeptForm({...deptForm, code: e.target.value.toUpperCase()})} className="rounded-none mt-1" /></div>
                        <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Description</Label>
                            <Input value={deptForm.description} onChange={(e) => setDeptForm({...deptForm, description: e.target.value})} className="rounded-none mt-1" /></div>
                        <div className="flex justify-end gap-3 pt-4">
                            <Button type="button" variant="outline" className="rounded-none" onClick={() => setDialogOpen(false)}>Cancel</Button>
                            <Button type="submit" className="bg-brand hover:bg-brand rounded-none" data-testid="save-dept-btn">Create Department</Button>
                        </div>
                    </form>
                );
            case 'designations':
                return (
                    <form onSubmit={(e) => { e.preventDefault(); handleCreate('designations', desigForm); }} className="space-y-4">
                        <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Designation Name</Label>
                            <Input value={desigForm.name} onChange={(e) => setDesigForm({...desigForm, name: e.target.value})} className="rounded-none mt-1" required data-testid="desig-name-input" /></div>
                        <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Code</Label>
                            <Input value={desigForm.code} onChange={(e) => setDesigForm({...desigForm, code: e.target.value.toUpperCase()})} className="rounded-none mt-1" /></div>
                        <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Level</Label>
                            <Input type="number" value={desigForm.level} onChange={(e) => setDesigForm({...desigForm, level: parseInt(e.target.value) || 1})} className="rounded-none mt-1" min="1" /></div>
                        <div className="flex justify-end gap-3 pt-4">
                            <Button type="button" variant="outline" className="rounded-none" onClick={() => setDialogOpen(false)}>Cancel</Button>
                            <Button type="submit" className="bg-brand hover:bg-brand rounded-none" data-testid="save-desig-btn">Create Designation</Button>
                        </div>
                    </form>
                );
            case 'shifts':
                return (
                    <form onSubmit={(e) => { e.preventDefault(); handleCreate('shifts', shiftForm); }} className="space-y-4">
                        <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Shift Name</Label>
                            <Input value={shiftForm.name} onChange={(e) => setShiftForm({...shiftForm, name: e.target.value})} className="rounded-none mt-1" required data-testid="shift-name-input" /></div>
                        <div className="grid grid-cols-2 gap-4">
                            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Start Time</Label>
                                <Input type="time" value={shiftForm.start_time} onChange={(e) => setShiftForm({...shiftForm, start_time: e.target.value})} className="rounded-none mt-1" required /></div>
                            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">End Time</Label>
                                <Input type="time" value={shiftForm.end_time} onChange={(e) => setShiftForm({...shiftForm, end_time: e.target.value})} className="rounded-none mt-1" required /></div>
                        </div>
                        <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Break Duration (mins)</Label>
                            <Input type="number" value={shiftForm.break_duration_minutes} onChange={(e) => setShiftForm({...shiftForm, break_duration_minutes: parseInt(e.target.value) || 0})} className="rounded-none mt-1" /></div>
                        <div className="flex items-center gap-2 mt-2">
                            <input type="checkbox" checked={shiftForm.is_night_shift} onChange={(e) => setShiftForm({...shiftForm, is_night_shift: e.target.checked})} id="night-shift" />
                            <Label htmlFor="night-shift" className="text-sm">Night Shift</Label>
                        </div>
                        <div className="flex justify-end gap-3 pt-4">
                            <Button type="button" variant="outline" className="rounded-none" onClick={() => setDialogOpen(false)}>Cancel</Button>
                            <Button type="submit" className="bg-brand hover:bg-brand rounded-none" data-testid="save-shift-btn">Create Shift</Button>
                        </div>
                    </form>
                );
            case 'salary':
                return (
                    <form onSubmit={(e) => { e.preventDefault(); handleCreate('salary-structures', salaryForm); }} className="space-y-4">
                        <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Structure Name</Label>
                            <Input value={salaryForm.name} onChange={(e) => setSalaryForm({...salaryForm, name: e.target.value})} className="rounded-none mt-1" required data-testid="salary-name-input" /></div>
                        <div className="grid grid-cols-3 gap-4">
                            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Basic %</Label>
                                <Input type="number" value={salaryForm.basic_percentage} onChange={(e) => setSalaryForm({...salaryForm, basic_percentage: parseFloat(e.target.value)})} className="rounded-none mt-1" /></div>
                            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">HRA %</Label>
                                <Input type="number" value={salaryForm.hra_percentage} onChange={(e) => setSalaryForm({...salaryForm, hra_percentage: parseFloat(e.target.value)})} className="rounded-none mt-1" /></div>
                            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">DA %</Label>
                                <Input type="number" value={salaryForm.da_percentage} onChange={(e) => setSalaryForm({...salaryForm, da_percentage: parseFloat(e.target.value)})} className="rounded-none mt-1" /></div>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">PF %</Label>
                                <Input type="number" value={salaryForm.pf_percentage} onChange={(e) => setSalaryForm({...salaryForm, pf_percentage: parseFloat(e.target.value)})} className="rounded-none mt-1" /></div>
                            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">ESI %</Label>
                                <Input type="number" step="0.01" value={salaryForm.esi_percentage} onChange={(e) => setSalaryForm({...salaryForm, esi_percentage: parseFloat(e.target.value)})} className="rounded-none mt-1" /></div>
                            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Prof Tax</Label>
                                <Input type="number" value={salaryForm.professional_tax} onChange={(e) => setSalaryForm({...salaryForm, professional_tax: parseFloat(e.target.value)})} className="rounded-none mt-1" /></div>
                        </div>
                        <div className="flex justify-end gap-3 pt-4">
                            <Button type="button" variant="outline" className="rounded-none" onClick={() => setDialogOpen(false)}>Cancel</Button>
                            <Button type="submit" className="bg-brand hover:bg-brand rounded-none" data-testid="save-salary-btn">Create Structure</Button>
                        </div>
                    </form>
                );
            case 'leave':
                return (
                    <form onSubmit={(e) => { e.preventDefault(); handleCreate('leave-structures', leaveForm); }} className="space-y-4">
                        <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Structure Name</Label>
                            <Input value={leaveForm.name} onChange={(e) => setLeaveForm({...leaveForm, name: e.target.value})} className="rounded-none mt-1" required data-testid="leave-struct-name-input" /></div>
                        <div className="grid grid-cols-3 gap-4">
                            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Annual Leaves</Label>
                                <Input type="number" value={leaveForm.annual_leaves} onChange={(e) => setLeaveForm({...leaveForm, annual_leaves: parseInt(e.target.value)})} className="rounded-none mt-1" /></div>
                            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Sick Leaves</Label>
                                <Input type="number" value={leaveForm.sick_leaves} onChange={(e) => setLeaveForm({...leaveForm, sick_leaves: parseInt(e.target.value)})} className="rounded-none mt-1" /></div>
                            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Casual Leaves</Label>
                                <Input type="number" value={leaveForm.casual_leaves} onChange={(e) => setLeaveForm({...leaveForm, casual_leaves: parseInt(e.target.value)})} className="rounded-none mt-1" /></div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Maternity Leaves</Label>
                                <Input type="number" value={leaveForm.maternity_leaves} onChange={(e) => setLeaveForm({...leaveForm, maternity_leaves: parseInt(e.target.value)})} className="rounded-none mt-1" /></div>
                            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Paternity Leaves</Label>
                                <Input type="number" value={leaveForm.paternity_leaves} onChange={(e) => setLeaveForm({...leaveForm, paternity_leaves: parseInt(e.target.value)})} className="rounded-none mt-1" /></div>
                        </div>
                        <div className="flex justify-end gap-3 pt-4">
                            <Button type="button" variant="outline" className="rounded-none" onClick={() => setDialogOpen(false)}>Cancel</Button>
                            <Button type="submit" className="bg-brand hover:bg-brand rounded-none" data-testid="save-leave-struct-btn">Create Structure</Button>
                        </div>
                    </form>
                );
            default: return null;
        }
    };

    const renderContent = () => {
        switch (activeTab) {
            case 'companies':
                return companies.length === 0 ? <EmptyState label="companies" /> : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {companies.map(c => (
                            <Card key={c._id} className="rounded-none border-slate-200" data-testid={`company-card-${c._id}`}>
                                <CardContent className="p-5">
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="p-2 bg-blue-100"><Building2 className="h-5 w-5 text-brand" /></div>
                                        <Badge className="rounded-none bg-slate-100 text-slate-700">{c.code}</Badge>
                                    </div>
                                    <h3 className="font-bold text-slate-900">{c.name}</h3>
                                    {c.address && <p className="text-sm text-slate-500 mt-1">{c.address}</p>}
                                    <div className="flex gap-4 mt-3 text-xs text-slate-400">
                                        {c.phone && <span>{c.phone}</span>}
                                        {c.email && <span>{c.email}</span>}
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                );
            case 'branches':
                return branches.length === 0 ? <EmptyState label="branches" /> : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {branches.map(b => (
                            <Card key={b._id} className="rounded-none border-slate-200">
                                <CardContent className="p-5">
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="p-2 bg-indigo-100"><GitBranch className="h-5 w-5 text-indigo-600" /></div>
                                        <div className="flex gap-2">
                                            <Badge className="rounded-none bg-slate-100 text-slate-700">{b.code}</Badge>
                                            {b.is_head_office && <Badge className="rounded-none bg-blue-100 text-blue-700">HQ</Badge>}
                                        </div>
                                    </div>
                                    <h3 className="font-bold text-slate-900">{b.name}</h3>
                                    {b.address && <p className="text-sm text-slate-500 mt-1">{b.address}</p>}
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                );
            case 'shifts':
                return shifts.length === 0 ? <EmptyState label="shifts" /> : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {shifts.map(s => (
                            <Card key={s._id} className="rounded-none border-slate-200">
                                <CardContent className="p-5">
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="p-2 bg-amber-100"><Clock className="h-5 w-5 text-amber-600" /></div>
                                        {s.is_night_shift && <Badge className="rounded-none bg-slate-800 text-white">Night</Badge>}
                                    </div>
                                    <h3 className="font-bold text-slate-900">{s.name}</h3>
                                    <p className="text-sm text-slate-600 mt-1">{s.start_time} - {s.end_time}</p>
                                    <p className="text-xs text-slate-400 mt-1">Break: {s.break_duration_minutes} mins</p>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                );
            default:
                return renderTableView();
        }
    };

    const renderTableView = () => {
        let items = [];
        let columns = [];
        switch (activeTab) {
            case 'departments':
                items = departments; columns = [{ key: 'name', label: 'Name' }, { key: 'code', label: 'Code' }, { key: 'description', label: 'Description' }]; break;
            case 'designations':
                items = designations; columns = [{ key: 'name', label: 'Name' }, { key: 'code', label: 'Code' }, { key: 'level', label: 'Level' }]; break;
            case 'salary':
                items = salaryStructures; columns = [{ key: 'name', label: 'Name' }, { key: 'basic_percentage', label: 'Basic %' }, { key: 'hra_percentage', label: 'HRA %' }, { key: 'da_percentage', label: 'DA %' }, { key: 'pf_percentage', label: 'PF %' }, { key: 'professional_tax', label: 'Prof Tax' }]; break;
            case 'leave':
                items = leaveStructures; columns = [{ key: 'name', label: 'Name' }, { key: 'annual_leaves', label: 'Annual' }, { key: 'sick_leaves', label: 'Sick' }, { key: 'casual_leaves', label: 'Casual' }, { key: 'maternity_leaves', label: 'Maternity' }, { key: 'paternity_leaves', label: 'Paternity' }]; break;
            default: return null;
        }

        if (items.length === 0) return <EmptyState label={activeTab} />;
        return (
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead><tr className="border-b border-slate-200 bg-slate-50">
                        {columns.map(c => <th key={c.key} className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 p-3">{c.label}</th>)}
                    </tr></thead>
                    <tbody>{items.map((item, i) => (
                        <tr key={item._id || i} className="border-b border-slate-100 hover:bg-slate-50">
                            {columns.map(c => <td key={c.key} className="p-3 text-sm text-slate-700">{item[c.key] || '-'}</td>)}
                        </tr>
                    ))}</tbody>
                </table>
            </div>
        );
    };

    const EmptyState = ({ label }) => (
        <div className="py-12 text-center text-slate-500">No {label} found. Create one to get started.</div>
    );

    const tabTitles = { companies: 'Company', branches: 'Branch', departments: 'Department', designations: 'Designation', shifts: 'Shift', salary: 'Salary Structure', leave: 'Leave Structure' };

    if (loading) return <div className="flex items-center justify-center h-64"><div className="text-slate-500">Loading company setup...</div></div>;

    return (
        <div className="space-y-6" data-testid="company-setup-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>Company Setup</h1>
                    <p className="text-slate-500 mt-1">Manage companies, branches, departments, shifts, and structures</p>
                </div>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-brand hover:bg-brand rounded-none" data-testid="add-new-btn">
                            <Plus className="h-4 w-4 mr-2" />New {tabTitles[activeTab]}
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="rounded-none max-w-lg max-h-[90vh] overflow-y-auto">
                        <DialogHeader><DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>Create {tabTitles[activeTab]}</DialogTitle></DialogHeader>
                        <div className="mt-4">{renderForm()}</div>
                    </DialogContent>
                </Dialog>
            </div>

            <Card className="rounded-none border-slate-200">
                <div className="border-b border-slate-200 overflow-x-auto">
                    <div className="flex min-w-max px-4">
                        {tabs.map(tab => (
                            <TabButton key={tab.id} active={activeTab === tab.id} onClick={() => setActiveTab(tab.id)} icon={tab.icon} label={tab.label} count={tab.count} />
                        ))}
                    </div>
                </div>
                <CardContent className="p-6">
                    {renderContent()}
                </CardContent>
            </Card>
        </div>
    );
};

export default CompanySetup;
